<script>
  import { onMount } from "svelte";
  import { portfolio } from "../../stores/portfolio.js";
  import { api } from "../../services/api.js";
  import CardPieChart from "../../components/Cards/CardPieChart.svelte";

  let loading = true;
  let historyLoading = false;
  let positionsLoading = false;
  let selectedPeriod = "1M";
  let selectedPositionPeriod = "1M";

  // Trade modal state
  let showTradeModal = false;
  let tradeLoading = false;
  let tradeError = "";
  let tradeSuccess = "";
  let tradeForm = {
    symbol: "",
    side: "buy",
    amountType: "cash",
    amount: "",
    orderType: "market",
    limitPrice: "",
    stopPrice: "",
  };

  // Symbol search state
  let symbolSearchResults = [];
  let symbolSearchLoading = false;
  let symbolSearchTimeout = null;

  const periods = [
    { value: "1D", label: "1D" },
    { value: "1W", label: "1W" },
    { value: "1M", label: "1M" },
    { value: "3M", label: "3M" },
    { value: "1Y", label: "1Y" },
    { value: "ALL", label: "ALL" },
  ];

  onMount(async () => {
    await Promise.all([
      portfolio.fetchSummary(),
      fetchPositionsWithHistory(selectedPositionPeriod),
      fetchHistory(selectedPeriod),
    ]);
    loading = false;
  });

  async function fetchHistory(period) {
    historyLoading = true;
    selectedPeriod = period;
    await portfolio.fetchHistory(period);
    historyLoading = false;
  }

  async function fetchPositionsWithHistory(period) {
    positionsLoading = true;
    selectedPositionPeriod = period;
    await portfolio.fetchPositionsWithHistory(period);
    positionsLoading = false;
  }

  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  function formatPercent(value) {
    const num = parseFloat(value) || 0;
    return (num >= 0 ? "+" : "") + num.toFixed(2) + "%";
  }

  // Trade modal functions
  function openTradeModal(position = null) {
    tradeError = "";
    tradeSuccess = "";
    symbolSearchResults = [];

    if (position) {
      tradeForm = {
        symbol: position.symbol,
        side: "buy",
        amountType: "cash",
        amount: "",
        orderType: "market",
        limitPrice: "",
        stopPrice: "",
      };
    } else {
      tradeForm = {
        symbol: "",
        side: "buy",
        amountType: "cash",
        amount: "",
        orderType: "market",
        limitPrice: "",
        stopPrice: "",
      };
    }
    showTradeModal = true;
  }

  function closeTradeModal() {
    showTradeModal = false;
    tradeError = "";
    tradeSuccess = "";
    symbolSearchResults = [];
  }

  async function searchSymbol(query) {
    if (!query || query.length < 1) {
      symbolSearchResults = [];
      return;
    }

    // Debounce search
    if (symbolSearchTimeout) {
      clearTimeout(symbolSearchTimeout);
    }

    symbolSearchTimeout = setTimeout(async () => {
      symbolSearchLoading = true;
      try {
        const results = await api.searchStocks(query, 8);
        symbolSearchResults = results || [];
      } catch (error) {
        console.error("Symbol search error:", error);
        symbolSearchResults = [];
      }
      symbolSearchLoading = false;
    }, 300);
  }

  function selectSymbol(symbol) {
    tradeForm.symbol = symbol;
    symbolSearchResults = [];
  }

  async function submitTrade() {
    tradeError = "";
    tradeSuccess = "";

    // Validation
    if (!tradeForm.symbol) {
      tradeError = "Please enter a stock symbol";
      return;
    }

    if (!tradeForm.amount || parseFloat(tradeForm.amount) <= 0) {
      tradeError = "Please enter a valid amount";
      return;
    }

    // Validate sell orders don't exceed owned position
    if (tradeForm.side === "sell") {
      if (!currentPosition) {
        tradeError = `You don't own any shares of ${tradeForm.symbol.toUpperCase()}`;
        return;
      }

      const amount = parseFloat(tradeForm.amount);

      if (tradeForm.amountType === "shares") {
        // Selling by shares - check against owned quantity
        const ownedQty = parseFloat(currentPosition.qty) || 0;
        if (amount > ownedQty) {
          tradeError = `You only own ${ownedQty.toFixed(4)} shares of ${tradeForm.symbol.toUpperCase()}. Cannot sell ${amount} shares.`;
          return;
        }
      } else {
        // Selling by cash - check against market value
        const marketValue = parseFloat(currentPosition.market_value) || 0;
        if (amount > marketValue) {
          tradeError = `Your ${tradeForm.symbol.toUpperCase()} position is worth ${formatCurrency(marketValue)}. Cannot sell ${formatCurrency(amount)}.`;
          return;
        }
      }
    }

    if (tradeForm.orderType === "limit" || tradeForm.orderType === "stop_limit") {
      if (!tradeForm.limitPrice || parseFloat(tradeForm.limitPrice) <= 0) {
        tradeError = "Please enter a valid limit price";
        return;
      }
    }

    if (tradeForm.orderType === "stop_limit") {
      if (!tradeForm.stopPrice || parseFloat(tradeForm.stopPrice) <= 0) {
        tradeError = "Please enter a valid stop price";
        return;
      }
    }

    tradeLoading = true;

    try {
      const payload = {
        symbol: tradeForm.symbol.toUpperCase(),
        side: tradeForm.side,
        amount_type: tradeForm.amountType,
        amount: tradeForm.amount,
        order_type: tradeForm.orderType,
      };

      if (tradeForm.orderType === "limit" || tradeForm.orderType === "stop_limit") {
        payload.limit_price = tradeForm.limitPrice;
      }

      if (tradeForm.orderType === "stop_limit") {
        payload.stop_price = tradeForm.stopPrice;
      }

      const result = await api.post("/portfolio/trade", payload);

      tradeSuccess = result.message || `Successfully submitted ${tradeForm.side} order for ${tradeForm.symbol.toUpperCase()}`;

      // Refresh portfolio data
      setTimeout(async () => {
        await Promise.all([
          portfolio.fetchSummary(),
          fetchPositionsWithHistory(selectedPositionPeriod),
        ]);

        // Close modal after brief success message
        setTimeout(() => {
          closeTradeModal();
        }, 2000);
      }, 1000);

    } catch (error) {
      tradeError = error.message || "Failed to execute trade";
    }

    tradeLoading = false;
  }

  // Get position for a symbol (for showing current holdings in trade modal)
  function getPositionForSymbol(symbol) {
    return $portfolio.positions.find(p => p.symbol.toUpperCase() === symbol.toUpperCase());
  }

  $: totalValue = $portfolio.summary?.total_value || $portfolio.summary?.equity || 0;
  $: portfolioAllocation = $portfolio.positions.map(p => ({
    symbol: p.symbol,
    percentage: parseFloat(((p.market_value / totalValue) * 100).toFixed(2)) || 0,
  }));

  $: periodGain = $portfolio.history?.period_gain || 0;
  $: periodGainPct = $portfolio.history?.period_gain_pct || 0;
  $: isGain = periodGain >= 0;

  $: currentPosition = tradeForm.symbol ? getPositionForSymbol(tradeForm.symbol) : null;
  $: availableCash = $portfolio.summary?.cash || 0;

  // Check if sell amount exceeds available position
  $: sellAmountExceedsPosition = (() => {
    if (tradeForm.side !== "sell" || !currentPosition || !tradeForm.amount) {
      return false;
    }
    const amount = parseFloat(tradeForm.amount);
    if (isNaN(amount) || amount <= 0) return false;

    if (tradeForm.amountType === "shares") {
      return amount > parseFloat(currentPosition.qty);
    } else {
      return amount > parseFloat(currentPosition.market_value);
    }
  })();

  // Sell all function
  function sellAll() {
    if (!currentPosition) return;

    tradeForm.side = "sell";
    if (tradeForm.amountType === "shares") {
      tradeForm.amount = parseFloat(currentPosition.qty).toString();
    } else {
      tradeForm.amount = parseFloat(currentPosition.market_value).toFixed(2);
    }
  }
</script>

<!-- Trade Modal -->
{#if showTradeModal}
  <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-ink/50 transition-opacity" on:click={closeTradeModal}></div>

      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

      <div class="inline-block align-bottom bg-surface rounded-2xl border border-line text-left overflow-visible shadow-sm transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="px-6 pt-6 pb-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-ink" id="modal-title">
              Manual Trade
            </h3>
            <button on:click={closeTradeModal} class="btn btn-ghost btn-sm !px-2" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>

          {#if tradeError}
            <div class="banner banner-danger mb-4">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {tradeError}
            </div>
          {/if}

          {#if tradeSuccess}
            <div class="banner banner-success mb-4">
              <i class="fas fa-check-circle mr-2"></i>
              {tradeSuccess}
            </div>
          {/if}

          <div class="space-y-4">
            <!-- Symbol Input -->
            <div class="relative">
              <label class="label">
                Stock symbol
              </label>
              <input
                type="text"
                bind:value={tradeForm.symbol}
                on:input={(e) => searchSymbol(e.target.value)}
                placeholder="e.g., AAPL"
                class="input uppercase"
                disabled={tradeLoading}
              />

              <!-- Symbol Search Results -->
              {#if symbolSearchResults.length > 0}
                <div class="absolute z-50 w-full mt-1 bg-surface border border-line rounded-xl shadow-sm max-h-48 overflow-y-auto">
                  {#each symbolSearchResults as result}
                    <button
                      type="button"
                      on:click={() => selectSymbol(result.symbol)}
                      class="w-full text-left px-4 py-2.5 hover:bg-surface-variant flex justify-between items-center"
                    >
                      <span class="text-sm font-semibold text-ink">{result.symbol}</span>
                      <span class="text-xs text-ink-tertiary truncate ml-2 max-w-xs">{result.name}</span>
                    </button>
                  {/each}
                </div>
              {/if}

              {#if symbolSearchLoading}
                <div class="absolute right-4 top-11">
                  <i class="fas fa-spinner fa-spin text-ink-tertiary"></i>
                </div>
              {/if}
            </div>

            <!-- Current Position Info (if applicable) -->
            {#if currentPosition}
              <div class="rounded-xl bg-surface-variant p-3 text-sm">
                <p class="text-ink-secondary">
                  <span class="font-semibold text-ink">Current position:</span>
                  <span class="tnum">{parseFloat(currentPosition.qty).toFixed(6)} shares @ {formatCurrency(currentPosition.avg_entry_price)}</span>
                </p>
                <p class="text-ink-secondary">
                  <span class="font-semibold text-ink">Market value:</span>
                  <span class="tnum">{formatCurrency(currentPosition.market_value)}</span>
                  <span class="tnum {parseFloat(currentPosition.unrealized_pl) >= 0 ? 'text-emerald-600' : 'text-red-600'}">
                    ({formatCurrency(currentPosition.unrealized_pl)})
                  </span>
                </p>
              </div>
            {/if}

            <!-- Buy/Sell Toggle -->
            <div>
              <label class="label">
                Action
              </label>
              <div class="flex rounded-xl overflow-hidden border border-line">
                <button
                  type="button"
                  on:click={() => tradeForm.side = "buy"}
                  class="flex-1 py-2 px-4 text-sm font-semibold transition-colors {tradeForm.side === 'buy'
                    ? 'bg-moira text-white'
                    : 'bg-surface text-ink-secondary hover:bg-surface-variant'}"
                  disabled={tradeLoading}
                >
                  Buy
                </button>
                <button
                  type="button"
                  on:click={() => tradeForm.side = "sell"}
                  class="flex-1 py-2 px-4 text-sm font-semibold transition-colors {tradeForm.side === 'sell'
                    ? 'bg-danger text-white'
                    : 'bg-surface text-ink-secondary hover:bg-surface-variant'}"
                  disabled={tradeLoading}
                >
                  Sell
                </button>
              </div>
            </div>

            <!-- Amount Type Toggle -->
            <div>
              <label class="label">
                Amount type
              </label>
              <div class="flex rounded-xl overflow-hidden border border-line">
                <button
                  type="button"
                  on:click={() => tradeForm.amountType = "cash"}
                  class="flex-1 py-2 px-4 text-sm font-semibold transition-colors {tradeForm.amountType === 'cash'
                    ? 'bg-accent text-white'
                    : 'bg-surface text-ink-secondary hover:bg-surface-variant'}"
                  disabled={tradeLoading}
                >
                  Dollars
                </button>
                <button
                  type="button"
                  on:click={() => tradeForm.amountType = "shares"}
                  class="flex-1 py-2 px-4 text-sm font-semibold transition-colors {tradeForm.amountType === 'shares'
                    ? 'bg-accent text-white'
                    : 'bg-surface text-ink-secondary hover:bg-surface-variant'}"
                  disabled={tradeLoading}
                >
                  Shares
                </button>
              </div>
            </div>

            <!-- Amount Input -->
            <div>
              <div class="flex justify-between items-center">
                <label class="label">
                  {tradeForm.amountType === "cash" ? "Dollar amount" : "Number of shares"}
                </label>
                {#if tradeForm.side === "sell" && currentPosition}
                  <button
                    type="button"
                    on:click={sellAll}
                    class="text-xs text-red-600 hover:text-red-700 font-semibold mb-1.5"
                    disabled={tradeLoading}
                  >
                    Sell all
                  </button>
                {/if}
              </div>
              <div class="relative">
                {#if tradeForm.amountType === "cash"}
                  <span class="absolute left-4 top-3 text-sm text-ink-tertiary">$</span>
                {/if}
                <input
                  type="number"
                  bind:value={tradeForm.amount}
                  placeholder={tradeForm.amountType === "cash" ? "100.00" : "10"}
                  step={tradeForm.amountType === "cash" ? "0.01" : "0.000001"}
                  min="0"
                  class="input tnum {tradeForm.amountType === 'cash' ? '!pl-8' : ''} {sellAmountExceedsPosition ? 'input-error' : ''}"
                  disabled={tradeLoading}
                />
              </div>
              {#if tradeForm.side === "buy" && tradeForm.amountType === "cash"}
                <p class="text-xs text-ink-tertiary mt-1.5 tnum">
                  Available: {formatCurrency(availableCash)}
                </p>
              {/if}
              {#if tradeForm.side === "sell" && currentPosition}
                <p class="text-xs mt-1.5 tnum {sellAmountExceedsPosition ? 'text-red-600 font-semibold' : 'text-ink-tertiary'}">
                  {#if tradeForm.amountType === "shares"}
                    Available: {parseFloat(currentPosition.qty).toFixed(6)} shares
                    {#if sellAmountExceedsPosition}
                      <span class="ml-1">(exceeds available!)</span>
                    {/if}
                  {:else}
                    Available: {formatCurrency(currentPosition.market_value)}
                    {#if sellAmountExceedsPosition}
                      <span class="ml-1">(exceeds available!)</span>
                    {/if}
                  {/if}
                </p>
              {/if}
              {#if tradeForm.side === "sell" && !currentPosition && tradeForm.symbol}
                <p class="help-error">
                  <i class="fas fa-exclamation-triangle mr-1"></i>
                  You don't own any shares of {tradeForm.symbol.toUpperCase()}
                </p>
              {/if}
            </div>

            <!-- Order Type -->
            <div>
              <label class="label">
                Order type
              </label>
              <select
                bind:value={tradeForm.orderType}
                class="select"
                disabled={tradeLoading}
              >
                <option value="market">Market Order</option>
                <option value="limit">Limit Order</option>
                <option value="stop_limit">Stop Limit Order</option>
              </select>
            </div>

            <!-- Limit Price (for limit and stop_limit orders) -->
            {#if tradeForm.orderType === "limit" || tradeForm.orderType === "stop_limit"}
              <div>
                <label class="label">
                  Limit price
                </label>
                <div class="relative">
                  <span class="absolute left-4 top-3 text-sm text-ink-tertiary">$</span>
                  <input
                    type="number"
                    bind:value={tradeForm.limitPrice}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    class="input tnum !pl-8"
                    disabled={tradeLoading}
                  />
                </div>
              </div>
            {/if}

            <!-- Stop Price (for stop_limit orders only) -->
            {#if tradeForm.orderType === "stop_limit"}
              <div>
                <label class="label">
                  Stop price
                </label>
                <div class="relative">
                  <span class="absolute left-4 top-3 text-sm text-ink-tertiary">$</span>
                  <input
                    type="number"
                    bind:value={tradeForm.stopPrice}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    class="input tnum !pl-8"
                    disabled={tradeLoading}
                  />
                </div>
              </div>
            {/if}
          </div>
        </div>

        <div class="border-t border-line px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            on:click={closeTradeModal}
            disabled={tradeLoading}
            class="btn btn-outline"
          >
            Cancel
          </button>
          <button
            type="button"
            on:click={submitTrade}
            disabled={tradeLoading}
            class="btn {tradeForm.side === 'buy' ? 'btn-primary' : 'btn-danger'}"
          >
            {#if tradeLoading}
              <i class="fas fa-spinner fa-spin"></i>
              Processing...
            {:else}
              {tradeForm.side === "buy" ? "Buy" : "Sell"} {tradeForm.symbol || "Stock"}
            {/if}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<header class="mb-6">
  <h1 class="text-2xl font-bold text-ink">Portfolio</h1>
  <p class="text-sm text-ink-secondary mt-1">Holdings, performance, and account balances.</p>
</header>

<div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
  <div class="xl:col-span-2 space-y-6">
    <div class="card overflow-hidden">
      <div class="px-6 pt-6 pb-4 flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <h3 class="text-lg font-semibold text-ink mr-2">Portfolio Holdings</h3>
          <button on:click={() => fetchPositionsWithHistory(selectedPositionPeriod)} class="btn btn-ghost btn-sm">
            <i class="fas fa-sync-alt"></i> Refresh
          </button>
          <button
            on:click={() => openTradeModal()}
            class="btn btn-primary btn-sm"
          >
            Trade
          </button>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Period</span>
          <div class="flex gap-1">
            {#each periods as period}
              <button
                on:click={() => fetchPositionsWithHistory(period.value)}
                class="px-2.5 py-1 text-xs font-semibold rounded-lg transition-colors {selectedPositionPeriod === period.value
                  ? 'bg-emerald-50 text-moira-dark'
                  : 'text-ink-secondary hover:bg-surface-variant'}"
                disabled={positionsLoading}
              >
                {period.label}
              </button>
            {/each}
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr>
              <th class="th">Symbol</th>
              <th class="th text-right">Quantity</th>
              <th class="th text-right">Avg Cost</th>
              <th class="th text-right">Market Value</th>
              <th class="th text-right">Total Gain/Loss</th>
              <th class="th text-right">{selectedPositionPeriod} Change</th>
              <th class="th text-right">% of Portfolio</th>
              <th class="th text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {#if loading || positionsLoading}
              {#each Array(5) as _}
                <tr>
                  <td class="td"><div class="skeleton h-4 w-14"></div></td>
                  <td class="td"><div class="skeleton h-4 w-20 ml-auto"></div></td>
                  <td class="td"><div class="skeleton h-4 w-16 ml-auto"></div></td>
                  <td class="td"><div class="skeleton h-4 w-20 ml-auto"></div></td>
                  <td class="td"><div class="skeleton h-4 w-24 ml-auto"></div></td>
                  <td class="td"><div class="skeleton h-4 w-16 ml-auto"></div></td>
                  <td class="td"><div class="skeleton h-4 w-12 ml-auto"></div></td>
                  <td class="td"><div class="skeleton h-4 w-16 ml-auto"></div></td>
                </tr>
              {/each}
            {:else if $portfolio.positions.length === 0}
              <tr>
                <td colspan="8" class="td">
                  <div class="py-10 text-center">
                    <i class="fas fa-inbox text-2xl text-ink-tertiary mb-3"></i>
                    <p class="text-sm text-ink-secondary">No positions yet. Create a pie and start investing.</p>
                  </div>
                </td>
              </tr>
            {:else}
              {#each $portfolio.positions as position}
                {@const periodChange = position.period_pct_change || 0}
                {@const isPeriodGain = periodChange >= 0}
                <tr class="hover:bg-canvas">
                  <td class="td">
                    <span class="text-sm font-semibold text-ink">{position.symbol}</span>
                  </td>
                  <td class="td text-right tnum">
                    {parseFloat(position.qty).toFixed(6)}
                  </td>
                  <td class="td text-right tnum">
                    {formatCurrency(position.avg_entry_price)}
                  </td>
                  <td class="td text-right tnum">
                    {formatCurrency(position.market_value)}
                  </td>
                  <td class="td text-right tnum">
                    <span class="{parseFloat(position.unrealized_pl) >= 0 ? 'text-emerald-600' : 'text-red-600'}">
                      {formatCurrency(position.unrealized_pl)}
                      <span class="text-xs">
                        ({(parseFloat(position.unrealized_plpc) * 100).toFixed(2)}%)
                      </span>
                    </span>
                  </td>
                  <td class="td text-right tnum">
                    <span class="{isPeriodGain ? 'text-emerald-600' : 'text-red-600'} font-medium">
                      {formatPercent(periodChange)}
                    </span>
                  </td>
                  <td class="td text-right tnum">
                    {totalValue > 0 ? ((position.market_value / totalValue) * 100).toFixed(2) : 0}%
                  </td>
                  <td class="td text-right">
                    <div class="inline-flex gap-1">
                      <button
                        on:click={() => { openTradeModal(position); tradeForm.side = "buy"; }}
                        class="btn btn-ghost btn-sm !px-2 text-emerald-600 hover:text-emerald-700"
                        title="Buy more {position.symbol}"
                      >
                        <i class="fas fa-plus"></i>
                      </button>
                      <button
                        on:click={() => { openTradeModal(position); tradeForm.side = "sell"; }}
                        class="btn btn-ghost btn-sm !px-2 text-red-600 hover:text-red-700"
                        title="Sell {position.symbol}"
                      >
                        <i class="fas fa-minus"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Historical Performance Card -->
    <div class="card card-pad">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 class="text-lg font-semibold text-ink">
          Historical Performance
        </h3>
        <div class="flex gap-1">
          {#each periods as period}
            <button
              on:click={() => fetchHistory(period.value)}
              class="px-2.5 py-1 text-xs font-semibold rounded-lg transition-colors {selectedPeriod === period.value
                ? 'bg-emerald-50 text-moira-dark'
                : 'text-ink-secondary hover:bg-surface-variant'}"
              disabled={historyLoading}
            >
              {period.label}
            </button>
          {/each}
        </div>
      </div>
      {#if historyLoading}
        <div class="space-y-3">
          <div class="skeleton h-4 w-24"></div>
          <div class="skeleton h-9 w-48"></div>
          <div class="skeleton h-6 w-28"></div>
        </div>
      {:else}
        <div class="flex flex-wrap items-center justify-between gap-6">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-1">
              {selectedPeriod} Gain/Loss
            </p>
            <p class="text-3xl font-bold tnum {isGain ? 'text-emerald-600' : 'text-red-600'}">
              {isGain ? '+' : ''}{formatCurrency(periodGain)}
            </p>
            <p class="text-lg tnum {isGain ? 'text-emerald-600' : 'text-red-600'}">
              {formatPercent(periodGainPct)}
            </p>
          </div>
          <div class="inline-flex items-center gap-3 px-4 py-3 rounded-xl border {isGain ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}">
            <i class="fas {isGain ? 'fa-arrow-up text-emerald-600' : 'fa-arrow-down text-red-600'} text-xl"></i>
            <div>
              <p class="text-xs font-medium {isGain ? 'text-emerald-700' : 'text-red-700'}">
                {isGain ? 'Portfolio Up' : 'Portfolio Down'}
              </p>
              <p class="text-sm font-semibold tnum {isGain ? 'text-emerald-700' : 'text-red-700'}">
                {formatPercent(periodGainPct)} in {selectedPeriod}
              </p>
            </div>
          </div>
        </div>
      {/if}
    </div>

    <!-- Account Summary Card -->
    <div class="card card-pad">
      <h3 class="text-lg font-semibold text-ink mb-4">
        Account Summary
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="rounded-xl bg-surface-variant p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Total Equity</p>
          <p class="text-xl font-bold text-ink tnum mt-1">{formatCurrency($portfolio.summary?.total_value || $portfolio.summary?.equity)}</p>
        </div>
        <div class="rounded-xl bg-surface-variant p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Cash</p>
          <p class="text-xl font-bold text-ink tnum mt-1">{formatCurrency($portfolio.summary?.cash)}</p>
        </div>
        <div class="rounded-xl bg-surface-variant p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Buying Power</p>
          <p class="text-xl font-bold text-ink tnum mt-1">{formatCurrency($portfolio.summary?.buying_power)}</p>
        </div>
      </div>
    </div>
  </div>

  <div>
    <CardPieChart title="Current Allocation" data={portfolioAllocation} />
  </div>
</div>
